import { FormattingService } from 'src/app/core/services/formatting.service';
import { Component, Input } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { NewPeriodSelectorConfigurationType } from '../../enums/newPeriodSelectorConfigurationType';
import { PeriodHelper } from '../../helpers/period.helper';
import { EntityType } from '../../enums/entityType';
import { DeviationGraphSettingsModel } from '../../models/deviationGraphSettingsModel';

@Component({
    selector: 'app-graph',
    templateUrl: './graph.component.html',
    styleUrls: ['./graph.component.scss'],
    standalone: false
})
export class GraphComponent {
  visualRange: [number, number];
  valueAxis: any[] = [];
  minimumValue: string;
  maximumValue: string;
  hasValidDataForAllPoints: boolean;

  @Input() title: string;
  @Input() chartHeight: number;
  @Input() chartWidth: number;
  @Input() chartMargin: number;
  @Input() cardGraph: number;
  @Input() isForHeader: boolean;

  _dataSource: any;
  get dataSource(): any {
    return this._dataSource;
  }
  @Input() set dataSource(value: any) {
    this._dataSource = value;
    if (value) {
      this.hasValidDataForAllPoints = this._dataSource.some(e => e.hasValidData === true);
      this._dataSource.forEach(element => {
        element.target = 0;
        if (!this.hasValidDataForAllPoints) {
          element.value = null;
        }
      });

      this.computeValues();

      this.valueAxis.push({
        position: 'left',
        visible: true,
        tick: { visible: false },
        grid: { visible: false },
        label: { visible: false },
        color: '#5bacf5',
        width: '3',
        visualRange: this.visualRange
      });
    }
  }

  _model: DeviationGraphSettingsModel;
  get model(): DeviationGraphSettingsModel {
    return this._model;
  }
  @Input() set model(value: DeviationGraphSettingsModel) {
    this._model = value;
  }

  constructor(private readonly formattingService: FormattingService,
    private readonly location: Location,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute){}

  private computeValues() {
    const maxAbsDataPoint = Math.max(...this._dataSource.map(p => Math.abs(p.value)));

    if (this.hasValidDataForAllPoints) {

      this.visualRange = maxAbsDataPoint ? [maxAbsDataPoint * -1, maxAbsDataPoint] : [-1, 1];

      const minValue = Math.min(...this._dataSource.map(p => p.value));
      this.minimumValue =
        this.formattingService.getDisplayFormattedNumber(
          minValue >= 0 ? this.visualRange[0]: minValue,
          false);

      const maxValue = Math.max(...this._dataSource.map(p => p.value));
      this.maximumValue =
        this.formattingService.getDisplayFormattedNumber(
          maxValue <= 0 ? this.visualRange[1] : maxValue,
          false);
    } else {
      this.visualRange = [-1, 1];
    }
  }

  getValuesPadding() {
    return this.chartMargin === 10 ? 'values-padding-big' : 'values-padding-small';
  }

  showDetailsGraph(event: any) {
    event.stopPropagation();
    const urlTree = this.router.parseUrl(this.location.path());
    const route = this.getPIDetailsGraphRoute();
    const queryParams = {
      cropCycle: this.model.entityType === EntityType.CropCycle
        ? this.model.entityId
        : null,
      periodSelectorConfiguration: this.getCurrentPeriodSelectorConfiguration(urlTree),
      periodSelectorInterval: urlTree.queryParams.periodSelectorType
        ? PeriodHelper.getPeriodIntervalFromPeriodType(
          urlTree.queryParams.periodSelectorType,
          (urlTree.queryParams.startDate !== undefined || urlTree.queryParams.periodSelectorStartDate !== undefined))
        : urlTree.queryParams.periodSelectorInterval,
      periodSelectorStartDate: urlTree.queryParams.startDate ?? urlTree.queryParams.periodSelectorStartDate,
      periodSelectorEndDate: urlTree.queryParams.endDate ?? urlTree.queryParams.periodSelectorEndDate,
      periodSelectorIntervalLength: urlTree.queryParams.weekNumber ?? urlTree.queryParams.periodSelectorIntervalLength
    };
    this.router.navigate(route, { queryParams });
  }

  getCurrentPeriodSelectorConfiguration(urlTree: UrlTree): any {
    if (urlTree.queryParams.periodSelectorConfiguration) {
      return urlTree.queryParams.periodSelectorConfiguration;
    }
    // todo: remove this when adding the new period selector to all pages
    return urlTree.toString().includes('compare-crop-cycles')
      ? NewPeriodSelectorConfigurationType.CompareCropCycle
      : NewPeriodSelectorConfigurationType.OldPeriodSelector;
  }

  getPIDetailsGraphRoute() {
    const organizationId = +this.activatedRoute.snapshot.paramMap.get('organizationId');
    if (this.model.resourceType !== null && this.model.resourceType !== undefined) {
      return ['manager', 'organizations', organizationId, 'performance-indicator', 'deviation-graph', 'entity-id',
        this.model.entityId, 'entity-type', this.model.entityType, 'resource-type',
        this.model.resourceType, 'characteristic-type', this.model.charactersticType];
    } else {
      return ['manager', 'organizations', organizationId, 'performance-indicator', 'deviation-graph', 'entity-id',
        this.model.entityId, 'entity-type', this.model.entityType, 'characteristic-type',
        this.model.charactersticType];
    }
  }
}
